<template>
  <div class="search-city-picker">
    <div ref="asyncPickerInput" class="search-city-picker-input">
      <VanSearch
        v-if="myProps.isShowSearch"
        v-model="query"
        left-icon=""
        :clearable="true"
        :placeholder="myProps.placeholder"
        @update:model-value="eventUpdateQuery"
      >
        <template #left-icon>
          <span class="iconfont icon-a-search_line1 size-6" />
        </template>
      </VanSearch>

      <!-- where city currentCheckboxList:
      {{ currentCheckboxList }} -->
    </div>
    <div
      ref="asyncPickerWrap"
      class="search-city-picker-wrap pt-11"
      :style="
        asyncPickerInputHeight ? `padding-top: ${asyncPickerInputHeight}px` : ''
      "
    >
      <!-- 多选 -->
      <VanCheckboxGroup
        v-if="myProps.multiple"
        v-model="currentCheckboxList"
        class="search-city-picker-list multiple"
        @change="eventCheckboxChange"
      >
        <VanCellGroup>
          <VanCell
            v-for="(option, index) in currentOptionList"
            :key="option[myProps.valueKey]"
            class="search-city-picker-list-item"
            :class="{
              'is-active': currentCheckboxList.includes(
                option[myProps.valueKey],
              ),
            }"
            clickable
            @click="eventCheckboxToggle(index)"
          >
            <template #title>
              <slot name="title" :option="option">
                <span>{{ option[myProps.labelKey] }}</span>
              </slot>
            </template>
            <template #right-icon>
              <VanCheckbox
                :key="option[myProps.valueKey]"
                :ref="(el) => setCheckboxRef(el, index)"
                :name="option[myProps.valueKey]"
                @click.stop
              >
                <template #icon="data">
                  <span
                    v-show="data.checked"
                    class="iconfont icon-a-check_fill1 text-[#FF6A00] size-6"
                  />
                </template>
              </VanCheckbox>
            </template>
          </VanCell>

          <slot
            v-if="!myProps.loading && currentOptionList.length === 0"
            name="empty"
          >
            <ClientOnly>
              <VanEmpty image="search" />
            </ClientOnly>
          </slot>
        </VanCellGroup>
      </VanCheckboxGroup>
      <!-- 单选 -->
      <VanRadioGroup
        v-else
        v-model="currentRadio"
        class="search-city-picker-list"
        @change="eventRadioChange"
      >
        <VanCellGroup>
          <VanCell
            v-for="(option, index) in currentOptionList"
            :key="option[myProps.valueKey]"
            class="search-city-picker-list-item"
            :class="{
              'is-active': currentRadio === option[myProps.valueKey],
            }"
            clickable
            @click="eventClickRadio(index, option[myProps.valueKey])"
          >
            <template #title>
              <slot name="title" :option="option">
                <span>{{ option[myProps.labelKey] }}</span>
                <span v-if="noCountry"
                  >,{{
                    isCn ? option.countryNameCn : option.countryNameEn
                  }}</span
                >
              </slot>
            </template>
            <template #right-icon>
              <VanRadio :name="option[myProps.valueKey]">
                <template #icon="data">
                  <span
                    v-show="data.checked"
                    class="iconfont icon-a-check_fill1 text-[#FF6A00] size-6"
                  />
                </template>
              </VanRadio>
            </template>
          </VanCell>
          <slot
            v-if="!myProps.loading && currentOptionList.length === 0"
            name="empty"
          >
            <ClientOnly>
              <VanEmpty image="search" />
            </ClientOnly>
          </slot>
        </VanCellGroup>
      </VanRadioGroup>
      <div v-if="myProps.loading" class="search-city-picker-loading flex-c">
        <IconLoading />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { debounce as debounceFn } from 'lodash-es'
  import type { CheckboxInstance } from 'vant'
  import { getCity } from '~/api/common'
  import { CITY_PAGE_SIZE } from './constant'

  const CHECKBOX_REF_KEY = 'CHECKBOX-'
  let isEvent = false // 组件内事件触发的

  const myProps = withDefaults(
    defineProps<{
      optionList: any[] // option 选项列表
      modelValue: string | number | boolean | any[] | any // 选中的值
      labelKey: string // for 标签的 key
      valueKey?: string // for 值的 key
      isShowSearch?: boolean // 是否展示搜索框
      placeholder?: string // 搜索框占位符
      query?: string // 搜索值
      multiple?: boolean // 是否开启多选
      debounce?: number // query debounce time
      loading?: boolean // 是否展示加载状态
      checkList?: any[]
      countryId?: number | string
    }>(),
    {
      labelKey: 'nameEn',
      valueKey: 'id',
      isShowSearch: true, // 默认展示搜索框
      placeholder: '', // 搜索框占位符默认为空
      query: '', // 搜索值默认空
      multiple: false, // 默认禁用多选
      debounce: 1000, // query 搜索防抖时间
      loading: false, // 默认不是加载

      countryId: '',

      checkList: () => [],
    },
  )

  const emit = defineEmits<{
    'update:model-value': [value: any] // 触发默认 v-model 更新
    'update:query': [query: string] // 触发 query 更新
    data: [isLoadMore: boolean] // 加载数据 isLoadMore 是否是加载下一页
    'click:item': [index: number] // 点击选项

    'update:check-list': [list: any[]] // 触发 checkbox 更新
  }>()

  const attrs = useAttrs()
  const query = toRef(myProps.query)

  // 从 custom popup 中获取是否打开 popup 的注入
  const isShowPopup = inject<Ref<boolean>>('isShowPopup')
  const slotKey = inject<Ref<string>>('slotKey')

  // 单选
  const currentRadio = ref()
  // 多选
  const currentCheckboxList = ref<any[]>(myProps.checkList || [])
  const checkboxRefData = ref<{
    [key: string]: CheckboxInstance
  }>({})

  const asyncPickerInput = ref<HTMLElement>()
  const asyncPickerInputHeight = ref<number>()

  const currentOptionList = computed(() => myProps.optionList)
  const noCountry = computed(() => !myProps.countryId)
  const { isCn } = useVipLogo()

  /**
   * 根据搜索内容加载数据列表
   */
  const emitEventData = debounceFn(
    (isLoadMore: boolean) => {
      emit('data', isLoadMore)
    },
    myProps.debounce,
    {
      leading: true, // 立即触发一次
    },
  )

  /**
   * 输入框内容发生变化时触发
   * @param String e 当前输入的值
   */
  function eventUpdateQuery(e: string) {
    // 更新当前输入的搜索信息的值
    emit('update:query', query.value)

    // 根据输入内容搜索列表
    emitEventData(false)
  }

  // 计算 input height 高度
  function computedInputHeight() {
    asyncPickerInputHeight.value = asyncPickerInput.value
      ? asyncPickerInput.value.clientHeight
      : 44
  }

  function eventRadioChange() {
    const current = myProps.optionList.find(
      (item) => item[myProps.valueKey] === currentRadio.value,
    )
    if (current) {
      emit('update:model-value', current)
    }
  }

  // 同步已选择的城市数据
  async function asyncCompanyCity(idList: (string | number)[] = []) {
    try {
      const params: any = {
        sort: 'nameEn',
        current: 1,
        size: noCountry.value ? 20 : CITY_PAGE_SIZE,
      }

      if (myProps.countryId) {
        // city 的数据需要 country 的值
        params.appointSearch = [
          {
            name: 'allCityByCountryId',
            val: String(myProps.countryId),
          },
        ]
      }

      if (idList.length > 0) {
        const data = {
          name: 'cityId' as const,
          val: JSON.stringify(idList),
        }

        if (params.appointSearch) {
          params.appointSearch.push(data)
        } else {
          params.appointSearch = [data]
        }
      }
      const result = await getCity(params)
      return result.records
    } catch (e) {
      console.error(e)
    } finally {
      // cityIsLoading.value = false
    }
  }

  function eventCheckboxChange() {
    const currentList = myProps.optionList.filter((item) =>
      currentCheckboxList.value.includes(item[myProps.valueKey]),
    )
    isEvent = true

    // 如果当前选择的城市还有值，则异步获取数据 否则清空数据
    if (currentCheckboxList.value.length > 0) {
      asyncCompanyCity(currentCheckboxList.value).then((res) => {
        emit('update:model-value', res)
      })
    } else {
      emit('update:model-value', null)
    }
  }

  function eventClickRadio(index: number, value: any) {
    // 再次点击同一个选项 取消选中
    if (currentRadio.value === value) {
      emit('update:model-value', null)
      return
    }
    currentRadio.value = value
    eventClickCell(index)
  }

  function eventClickCell(index: number) {
    emit('click:item', index)
  }

  function eventCheckboxToggle(index: number) {
    const currentCheckbox = checkboxRefData.value[`${CHECKBOX_REF_KEY}${index}`]

    if (currentCheckbox && currentCheckbox.toggle) {
      currentCheckbox.toggle()
    }
    eventClickCell(index)
  }

  function setCheckboxRef(
    el: Element | ComponentPublicInstance | null,
    index: number,
  ) {
    if (el) {
      checkboxRefData.value[`${CHECKBOX_REF_KEY}${index}`] =
        el as CheckboxInstance
    }
  }

  // 初始化回调
  function initCallback() {
    emitEventData(false) // 加载数据
    nextTick(() => {
      computedInputHeight()
    })
  }

  // 监听元素触底
  function loadMore(this: HTMLElement) {
    if (!myProps.loading && isScrollReachBottom(this)) {
      emit('data', true)
    }
  }
  // 引用异步选择器的HTML元素
  const asyncPickerWrap = ref<HTMLElement>()
  useAsyncPickerWrapper(asyncPickerWrap, loadMore)

  watch(
    () => myProps.modelValue,
    (nv) => {
      if (myProps.multiple && !isEvent) {
        // 多选
        if (nv && nv.length > 0) {
          // 有值
          currentCheckboxList.value = nv.map(
            (item: any) => item[myProps.valueKey],
          )
        } else {
          // 无值
          currentCheckboxList.value = []
        }
      } else {
        // 单选
        if (nv && nv[myProps.valueKey]) {
          // 有值
          currentRadio.value = nv[myProps.valueKey]
        } else {
          // 无值
          currentRadio.value = null
        }
      }

      isEvent = false
    },
    { immediate: true },
  )

  watch(
    () => myProps.query,
    (nv) => (query.value = nv),
  )

  // 监听是否打开 popup
  watch(
    () => (isShowPopup ? isShowPopup.value : ''),
    (nv) => {
      if (nv) {
        // 打开了
        const currentSlotKey = attrs['slot-key'] || ''
        const injectSlotKey = slotKey?.value || ''
        // slot key 都相同 默认都是空字符串
        if (currentSlotKey === injectSlotKey) {
          initCallback()
        }
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    if (!isShowPopup) {
      // 没有注入直接执行一次方法
      initCallback()
    }
  })
</script>

<style lang="scss" scoped>
  .search-city-picker {
    position: relative;
    height: 100%;
    min-height: 0;

    .search-city-picker-input {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      z-index: 999;

      :deep(.van-search) {
        padding-left: 8px;
        margin-right: 16px;
        margin-left: 16px;
        background: #f8f9fd;
        border-radius: 4px;

        .van-search__content {
          padding-left: 0;

          .van-cell {
            height: 24px;

            .van-field__body {
              font-size: 14px;
            }
          }
        }
      }

      .search-icon {
        font-size: 24px;
        color: #9fb0c1;
      }
    }

    .search-city-picker-wrap {
      height: 100%;
      min-height: 0;
      overflow: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-y;

      .search-city-picker-list {
        :deep(.van-cell-group) {
          &::after {
            display: none;
          }
        }

        .search-city-picker-list-item {
          padding-top: 13px;
          padding-bottom: 13px;

          &.is-active {
            color: #ff6a00;
          }

          &::after {
            display: none;
          }
        }
      }
    }

    .search-city-picker-loading {
      padding: 20px;
      text-align: center;
    }

    .search-city-picker-icon {
      font-size: 24px;
      color: #ff6a00;
      fill: #ff6a00;
    }
  }
</style>
